import {
	useMutation,
	UseMutationResult,
	useQuery,
	UseQueryResult,
} from '@tanstack/react-query'

import { UpdateAccountRequestDTO } from '@/core/api/dtos/request/account/update'
import { UpdateBrandProfileRequestDTO } from '@/core/api/dtos/request/account/updateBrandProfile'
import { Account } from '@/core/api/entities/account/account'
import { AccountMapper } from '@/core/api/mappers/account/accountMapper'
import { accountService } from '@/core/api/services/account/accountService'

import { DEFAULT_PARAMS } from '@core/hooks/common'

/**
 * Get zones
 */
export const useGetAccount = ({
	accountId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Account, Error> => {
	const QUERY_KEY = [
		'account',
		accountId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			accountService
				.getAccount(accountId, config)
				.then(({ data: response }) => {
					return AccountMapper.toEntity(response.data)
				}),
		...options,
	})
}

/**
 * List and paginate accounts
 */
export const useListAccounts = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	{ data: Account[]; meta?: any },
	Error
> => {
	const QUERY_KEY = ['accounts', 'list', config?.params]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			accountService.listAccounts(config).then(({ data: response }) => {
				return {
					data: AccountMapper.toEntities(response?.data || []),
					meta: response?.meta,
				}
			}),
		...options,
	})
}

/**
 * Hook to update brand profile for an account
 */
export const useAccountPatch = ({
	accountId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	any,
	Error,
	UpdateAccountRequestDTO
> => {
	return useMutation({
		mutationFn: (data: UpdateAccountRequestDTO) => {
			return accountService.updateAccount(accountId, data, config)
		},
		...options,
	})
}

/**
 * Hook to update brand profile for an account
 */
export const useUpdateBrandProfile = ({
	accountId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	any,
	Error,
	UpdateBrandProfileRequestDTO
> => {
	return useMutation({
		mutationFn: (data: UpdateBrandProfileRequestDTO) => {
			return accountService.updateBrandProfile(accountId, data, config)
		},
		...options,
	})
}
